/**
 * Customer Group Detail Page
 *
 * Shows group-level information for a CompanyGroup, including
 * aggregate stats and member companies with links to their detail pages.
 * CR-4579
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { authAPI, coreAPI, CompanyGroup, CompanyGroupMembersResponse, UserProfile } from '../lib/api';
import { useFormatters } from '../contexts/PreferencesContext';
import { useAuth } from '../contexts/AuthContext';
import { usePortalMode } from '../hooks/usePortalMode';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Loader2,
  ArrowLeft,
  Network,
  Globe,
  ExternalLink,
  UserPlus,
  ChevronRight,
} from 'lucide-react';
import { UserDetailsModal } from './admin/UserDetailsModal';
import { useUserActions } from './admin/useUserActions';
import { EditUserProfileDialog } from './admin/EditUserProfileDialog';
import { GroupStatsCards } from './customer-group/GroupStatsCards';
import { GroupMemberCompaniesCard } from './customer-group/GroupMemberCompaniesCard';
import { GroupUsersCard } from './customer-group/GroupUsersCard';

export default function CustomerGroupDetail() {
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();
  const { formatCurrencyFromBase } = useFormatters();
  const { user, startImpersonation } = useAuth();
  const { isSupplier, isHelix } = usePortalMode();
  const userActions = useUserActions();
  const isSelf = (userProfile: { user: { id: number } }) => userProfile.user.id === user?.id;
  const isSuperAdmin = Boolean(user?.is_superuser);
  const canImpersonate = isHelix || isSupplier;

  const [group, setGroup] = useState<CompanyGroup | null>(null);
  const [companies, setCompanies] = useState<CompanyGroupMembersResponse['companies']>([]);
  const [stats, setStats] = useState<CompanyGroupMembersResponse['stats'] | null>(null);
  const [groupUsers, setGroupUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [impersonating, setImpersonating] = useState<number | null>(null);

  const totalUsers = useMemo(
    () => companies.reduce((sum, company) => sum + company.user_count, 0),
    [companies],
  );

  const fetchGroupData = useCallback(async () => {
    if (!groupId) return;
    setLoading(true);
    setError(null);
    try {
      const id = parseInt(groupId, 10);
      const [members, users] = await Promise.all([
        coreAPI.getCompanyGroupMembers(id),
        coreAPI.getCompanyGroupUsers(id).catch(() => ({ data: { count: 0, results: [] as UserProfile[] } })),
      ]);
      setGroup(members.data.group);
      setCompanies(members.data.companies);
      setStats(members.data.stats);
      setGroupUsers(users.data.results);
    } catch (err: unknown) {
      console.error('Failed to fetch group data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load group details');
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  useEffect(() => {
    fetchGroupData();
  }, [fetchGroupData]);

  const handleInviteUser = () => {
    if (!group) return;
    navigate(`/users/invite?type=customer&group=${group.id}`);
  };

  const handleImpersonate = async (u: UserProfile) => {
    if (!canImpersonate) return;
    try {
      setImpersonating(u.user.id);
      const response = await authAPI.impersonate(u.user.id, undefined, u.company);
      const impersonationData = response.data.data;
      const contextCompany = u.company
        ? { id: u.company, name: u.company_name }
        : undefined;
      startImpersonation(
        impersonationData.target_user,
        impersonationData.target_token,
        impersonationData.original_token,
        contextCompany,
      );
    } catch (err: unknown) {
      console.error('Error impersonating user:', err);
    } finally {
      setImpersonating(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error || !group) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Error: {error || 'Group not found'}</p>
          <Button onClick={() => navigate('/customers')} variant="outline" className="mt-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Customers
          </Button>
        </div>
      </div>
    );
  }

  const metaParts = [
    group.headquarters_country,
    group.website,
  ].filter(Boolean);

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1 text-sm text-muted-foreground" aria-label="Breadcrumb">
        <Link to="/customers" className="hover:text-foreground transition-colors">Customers</Link>
        <ChevronRight className="h-4 w-4" />
        <Link to="/customers?tab=groups" className="hover:text-foreground transition-colors">Customer Groups</Link>
        <ChevronRight className="h-4 w-4" />
        <span className="text-foreground font-medium truncate">{group.name}</span>
      </nav>

      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate('/customers?tab=groups')} aria-label="Back to customer groups">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-3 mb-1">
              <Network className="h-6 w-6 text-primary shrink-0" />
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight truncate">{group.name}</h1>
              <Badge className={group.is_active
                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
              }>
                {group.is_active ? 'Active' : 'Inactive'}
              </Badge>
            </div>
            <p className="text-muted-foreground">Customer Group</p>
            {metaParts.length > 0 && (
              <p className="mt-1 text-sm text-muted-foreground truncate">
                {group.headquarters_country && (
                  <span className="inline-flex items-center gap-1 mr-3">
                    <Globe className="h-3.5 w-3.5" />
                    {group.headquarters_country}
                  </span>
                )}
                {group.website && (
                  <a
                    href={group.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-primary hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    Website
                  </a>
                )}
              </p>
            )}
          </div>
        </div>
        <Button onClick={handleInviteUser} className="shrink-0 self-start">
          <UserPlus className="h-4 w-4 mr-2" />
          Add User
        </Button>
      </div>

      {group.description && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">About this group</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">{group.description}</p>
          </CardContent>
        </Card>
      )}

      {stats && (
        <GroupStatsCards stats={stats} totalUsers={totalUsers} formatCurrencyFromBase={formatCurrencyFromBase} />
      )}

      <div className="grid gap-6 xl:grid-cols-2">
        <GroupMemberCompaniesCard companies={companies} />
        <GroupUsersCard
          users={groupUsers}
          groupId={group.id}
          canImpersonate={canImpersonate}
          impersonating={impersonating}
          isSelf={isSelf}
          onAddUser={handleInviteUser}
          onOpenUser={userActions.openUserDetails}
          onImpersonate={handleImpersonate}
        />
      </div>

      <UserDetailsModal
        open={userActions.showUserDetails}
        onOpenChange={(openVal) => {
          userActions.setShowUserDetails(openVal);
          if (!openVal) fetchGroupData();
        }}
        selectedUser={userActions.selectedUser}
        userDetailTab={userActions.userDetailTab}
        setUserDetailTab={userActions.setUserDetailTab}
        userSitePermissions={userActions.userSitePermissions}
        savingPermission={userActions.savingPermission}
        isSuperAdmin={isSuperAdmin}
        actionLoading={userActions.actionLoading}
        isSelf={isSelf}
        handleToggleActive={userActions.handleToggleActive}
        openChangeRoleDialog={userActions.openChangeRoleDialog}
        handleToggleAdmin={userActions.handleToggleAdmin}
        handleToggleNotification={userActions.handleToggleNotification}
        handleToggleSiteLink={userActions.handleToggleSiteLink}
        handleToggleSitePermission={userActions.handleToggleSitePermission}
        handleApproveUser={userActions.handleApproveUser}
        handleRejectUser={userActions.handleRejectUser}
        handleSendInvitation={userActions.handleSendInvitation}
        openEditProfileDialog={userActions.openEditProfileDialog}
        handleAdminResetPassword={userActions.handleAdminResetPassword}
      />
      <EditUserProfileDialog
        open={userActions.showEditProfileDialog}
        onOpenChange={userActions.setShowEditProfileDialog}
        selectedUser={userActions.selectedUser}
        availableRoles={userActions.availableRoles}
        actionLoading={userActions.actionLoading}
        onSave={userActions.handleUpdateProfile}
      />
    </div>
  );
}
